import data from "./serverMessages.json";

/** Refus du serveur propres à une zone libre (docs/14 §2.11, §4.3), reconnus à leur message (copie vérifiée du
 *  serveur, `serverMessages.json`) pour désigner l'action attendue au lieu d'un simple message d'erreur :
 *  - `ZONE_NOT_READY` : terrain en file ou en préparation, préparation en échec ou annulée (positions, missions,
 *    ancrages, potentiel, exports, analyse du terrain, ordre recomposé sur le terrain précédent) ;
 *  - `ZONE_NO_LAND` : zone prête mais sans terre exploitable (dispositif refusé) ;
 *  - `ZONE_BUSY` : la ligne de la zone est tenue un instant par l'écriture finale du service (réessayer) ;
 *  - `CORRIDOR_OUTSIDE` : fuseau tracé hors de l'empreinte préparée (« Étendre la zone ») ;
 *  - `ZONE_EXTENDING` : code de transmission refusé pendant une extension (attendre sa fin).
 *  Ces refus ne sont pas des conflits d'écriture : l'historique de la carte n'est pas remis à zéro. */

export type ZoneRefusalCode = "ZONE_NOT_READY" | "ZONE_NO_LAND" | "ZONE_BUSY" | "CORRIDOR_OUTSIDE" | "ZONE_EXTENDING";

const BY_NAME = new Map(data.messages.map((m) => [m.name, m.fr]));
const BY_MESSAGE = new Map<string, ZoneRefusalCode>();
for (const [code, names] of Object.entries(data.codes)) {
  for (const name of names) {
    const message = BY_NAME.get(name);
    if (message) BY_MESSAGE.set(message, code as ZoneRefusalCode);
  }
}

/** Code du refus d'après le message du serveur (en français, avant traduction), null pour tout autre message. */
export function zoneRefusalCode(message: string | null | undefined): ZoneRefusalCode | null {
  return message ? (BY_MESSAGE.get(message.trim()) ?? null) : null;
}

/** Code du refus porté par une erreur de l'API (`ApiError.raw`), null sinon. */
export function zoneRefusalOf(error: unknown): ZoneRefusalCode | null {
  if (!error || typeof error !== "object") return null;
  const raw = (error as { raw?: unknown }).raw;
  return typeof raw === "string" ? zoneRefusalCode(raw) : null;
}

/** Refus « terrain pas prêt » reçu avant que la zone soit prête : sans objet une fois la zone prête (plus de zone
 *  en attente), il n'est plus affiché. Une zone sans terre exploitable (zone prête) reste signalée. */
export function outdatedRefusal(error: unknown, pendingZone: unknown): boolean {
  return zoneRefusalOf(error) === "ZONE_NOT_READY" && !pendingZone;
}

/** Bandeau d'attente de la mise en ambiance (un seul) : l'OPO périmé (`Order.stale`) s'il existe, sinon son
 *  indisponibilité quand c'est un refus de zone (terrain en préparation, échec, zone sans terre) ; dans les deux
 *  cas, le bandeau « Données initiales » d'une indisponibilité de zone n'est pas affiché. */
export function briefingZoneBanner(
  stale: string | null | undefined,
  unavailable: string | null | undefined,
): { message: string | null; hideUnavailable: boolean } {
  const zoneUnavailable = zoneRefusalCode(unavailable) !== null;
  return { message: stale || (zoneUnavailable ? (unavailable ?? null) : null), hideUnavailable: zoneUnavailable };
}
